import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { faTimes, faCircleInfo, faPlus } from '@fortawesome/free-solid-svg-icons';
import { NgbModal, NgbTimepickerConfig } from '@ng-bootstrap/ng-bootstrap';
import { AlertService } from 'src/app/alert.service';
import { AuthService } from 'src/app/auth.service';
import { FileService } from 'src/app/file.service';
import { SettingService } from 'src/app/setting.service';
import { HelpdeskService } from '../helpdesk.service';
import { NgIf, NgClass, NgFor } from '@angular/common';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';

@Component({
    selector: 'app-report-form',
    templateUrl: './report-form.component.html',
    styleUrls: ['./report-form.component.css'],
    imports: [FaIconComponent, NgIf, ReactiveFormsModule, NgClass, NgFor]
})
export class ReportFormComponent implements OnChanges, OnInit {

  // Properties
  bugReportForm: FormGroup;
  fileArray: any[] = [];
  @Input() bugReport: any = null;
  @Input() isOpenFromModal: boolean = false;
  @Output() newTicketEmit = new EventEmitter();

  // Font Awesome Properties
  faPlus = faPlus
  faCircleInfo = faCircleInfo;
  faTimes = faTimes;

  supportType: any[] = [
    {
      name: "Bug Report",
      value: 'BUG'
    },
    {
      name: "Support Ticket",
      value: 'SUPPORT'
    },
    {
      name: "Feature Request",
      value: 'FEATURE'
    }
  ]

  fileObject: any = {
    filename: '',
    size: '',
    type: '',
    file: ''
  }
  uploadedFile: any;
  uploadedFileBase64: any;
  user: any;

  // @ViewChild(BugReportTableComponent) bugReportTableComponent:BugReportTableComponent;

  constructor(private helpdeskService: HelpdeskService,
    private formBuilder: FormBuilder,
    private alertService: AlertService,
    private settingService: SettingService,
    private authService: AuthService,
    private fileService: FileService,
    private modalService: NgbModal,
    config: NgbTimepickerConfig) {
    config.seconds = true;
    config.spinners = false;
  }

  ngOnInit(): void {
    this.getUser();
    this.rebuildForm();
    this.supportTypeSubscription();
    this.getOrganizationSettings();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if(changes && changes.bugReport) {
      if(changes.bugReport.currentValue) {
        this.rebuildForm();
        this.bugReportForm.disable()
      } else {
        if(this.bugReportForm){
          this.resetBugReportForm();
          this.bugReportForm.enable()

        }        
      }
    }
  }

  newTicket(){
    this.newTicketEmit.emit();
  }

  rebuildForm() {
    this.bugReportForm = this.formBuilder.group({
      name: [this.bugReport ? this.bugReport.name : '', [Validators.required]],
      companyName: [this.bugReport ? this.bugReport.companyName : '', [Validators.required]],
      phone: [this.bugReport ? this.bugReport.phone : '', [Validators.required]],
      email: [this.bugReport ? this.bugReport.email : '', [Validators.required]],
      supportType: [this.bugReport ? this.bugReport.supportType : null, [Validators.required]],
      files: [null],
      dateTimeBug: [this.bugReport ? this.bugReport.dateTimeBug : null],
      description: [this.bugReport ? this.bugReport.description : null, [Validators.required]],
      report: [''],
      title: [this.bugReport ? this.bugReport.title : '', [Validators.required]],
      steps: [this.bugReport ? this.bugReport.steps : ''],
      url: [this.bugReport ? this.bugReport.url : ''],
      // feature
    });
  }

  capitalizeFirstLetter(str) {
    return str[0].toUpperCase() + str.slice(1);
  }

  private getOrganizationSettings(): void {
    this.settingService.getOrganizationSettings().subscribe((organizationSettings) => {
      this.bugReportForm.controls['companyName'].setValue(organizationSettings.name)
    });
  }

  private async getUser() {
    this.user = await this.authService.getCurrentUser();
    this.bugReportForm.controls['name'].setValue(`${this.user.firstName} ${this.user.lastName}`)
  }

  private supportTypeSubscription() {
    this.bugReportForm.get('supportType').valueChanges.subscribe(data => {

      this.bugReportForm.controls['steps'].clearValidators();

      if (data?.val == 1) {
        this.bugReportForm.controls['steps'].setValidators([Validators.required]);
      }

      this.bugReportForm.controls['steps'].updateValueAndValidity();
    });
  }

  // Add Bug Report
  addBugReport(): void {
    let organizationId = null;
    let userId = null;
    if (this.bugReportForm.valid) {
      const user = JSON.parse(sessionStorage.getItem('CurrentUser'));
      if(user) {
        organizationId = user.organizationId;
        userId = user.id;
      }
      const body = {
        ...this.bugReportForm.value,
        organizationId,
        userId,
        report: this.bugReportForm.value.description,
        files: this.fileArray,
        reportedFrom: 'CRM'
      };
      this.helpdeskService.addReport(body).subscribe(() => {
        this.alertService.showSuccessAlert('Report Submitted');
        this.resetBugReportForm();
        this.newTicket();
      });
    } else {
      this.bugReportForm.markAllAsTouched();
    }
  }

  onFileChangeEvent(event: any, dropped = false): void {
    const uploaded = dropped ? event : event.target.files;

    if (uploaded.length !== 0 && this.fileArray.length < 3) {
      for (let i = 0; i < uploaded.length; i++) {

        let fileObject: any = {
          filename: '',
          size: '',
          type: '',
          file: ''
        }

        fileObject.filename = uploaded[i].name;
        fileObject.type = uploaded[i].type;
        fileObject.size = uploaded[i].size
        const reader = new FileReader();
        reader.readAsDataURL(uploaded[i]);
        reader.onload = (_event) => {
          fileObject.file = reader.result;
          this.fileArray.push(fileObject)
        }
      }
    } else {
      this.alertService.showErrorAlert("Only 3 files are allowed to be uploaded. Please delete a file to upload one.");
    }
  }

  removeImageInArray(i) {
    this.fileArray.splice(i, 1)
  }

  // Reset Bug Report Form
  private resetBugReportForm(): void {
    this.bugReportForm.reset();
    this.bugReportForm.controls.title.setValue(null);
    this.bugReportForm.controls.report.setValue(null);
  }

  // Get Status Color
  getStatusColor(status: string): string {
    switch (status) {
      case 'OPEN':
        return 'green'
      case 'IN REVIEW':
        return 'darkorange';
      case 'CLOSED':
        return 'red';
      default:
        return 'black';
    }
  }

  get name_validators() {
    return this.bugReportForm.get('name');
  }
  get companyName_validators() {
    return this.bugReportForm.get('companyName');
  }
  get phone_validators() {
    return this.bugReportForm.get('phone');
  }
  get email_validators() {
    return this.bugReportForm.get('email');
  }
  get supportType_validators() {
    return this.bugReportForm.get('supportType');
  }
  get description_validators() {
    return this.bugReportForm.get('description');
  }
  get title_validators() {
    return this.bugReportForm.get('title');
  }
}
